import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../lib/auth';
import { pharmacyAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Pill, Layers, Receipt, AlertTriangle, Plus, Search, Trash2, Printer, IndianRupee,
  TrendingUp, AlertCircle, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function PharmacyPage() {
  const { user, hasPermission } = useAuth();
  const [tab, setTab] = useState('drugs');
  const [drugs, setDrugs] = useState([]);
  const [batches, setBatches] = useState([]);
  const [dispenses, setDispenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Dialog state
  const [drugDialogOpen, setDrugDialogOpen] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [dispenseDialogOpen, setDispenseDialogOpen] = useState(false);
  const [printDispense, setPrintDispense] = useState(null);

  const canManageDrugs = hasPermission('pharmacy.manage_drugs');
  const canManageInventory = hasPermission('pharmacy.manage_inventory');
  const canDispense = hasPermission('pharmacy.dispense');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dr, br, dp, sm] = await Promise.all([
        pharmacyAPI.listDrugs(),
        pharmacyAPI.listBatches(),
        pharmacyAPI.listDispenses(),
        pharmacyAPI.summary(),
      ]);
      setDrugs(dr.data || []);
      setBatches(br.data || []);
      setDispenses(dp.data || []);
      setSummary(sm.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load pharmacy data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredDrugs = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return drugs;
    return drugs.filter(d =>
      (d.name || '').toLowerCase().includes(s) ||
      (d.generic_name || '').toLowerCase().includes(s)
    );
  }, [drugs, search]);

  if (!user?.is_super_admin && !hasPermission('pharmacy.view')) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="pharmacy-restricted">
        <Card className="max-w-lg w-full">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100"><AlertTriangle className="h-5 w-5 text-amber-600" /></div>
              <div>
                <CardTitle>Access Restricted</CardTitle>
                <CardDescription>You don't have the <code>pharmacy.view</code> permission.</CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="pharmacy-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Pharmacy</h1>
          <p className="mt-1 text-slate-600">Drug master, batch inventory, and dispensing — with FIFO-by-expiry allocation.</p>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard testid="sum-drugs" icon={Pill} color="emerald" label="Drugs" value={summary.total_drugs} sub="active in master" />
          <SummaryCard testid="sum-low" icon={AlertCircle} color="amber" label="Low Stock" value={summary.low_stock.length} sub="below reorder level" />
          <SummaryCard testid="sum-near-exp" icon={Calendar} color="rose" label="Near Expiry" value={summary.near_expiry_batches} sub={`${summary.expired_batches} already expired`} />
          <SummaryCard testid="sum-today" icon={TrendingUp} color="indigo" label="Today's Revenue" value={`₹${summary.today_revenue.toLocaleString('en-IN')}`} sub={`${summary.today_dispense_count} dispenses`} />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="pharmacy-tabs">
          <TabsTrigger value="drugs" data-testid="tab-drugs"><Pill className="h-4 w-4 mr-2" />Drug Master</TabsTrigger>
          <TabsTrigger value="batches" data-testid="tab-batches"><Layers className="h-4 w-4 mr-2" />Batches</TabsTrigger>
          <TabsTrigger value="dispenses" data-testid="tab-dispenses"><Receipt className="h-4 w-4 mr-2" />Dispensing</TabsTrigger>
        </TabsList>

        {/* ============ DRUG MASTER TAB ============ */}
        <TabsContent value="drugs" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name or generic…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-10"
                data-testid="drug-search"
              />
            </div>
            {canManageDrugs && (
              <DrugDialog open={drugDialogOpen} setOpen={setDrugDialogOpen} onSaved={fetchAll} />
            )}
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              {loading ? <Skeleton className="h-64 m-4" /> : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Drug</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Strength</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Form</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Manufacturer</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">Stock</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">Reorder</th>
                      <th className="text-center px-4 py-2 font-semibold text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDrugs.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400 italic">No drugs yet. Click "Add Drug" to start.</td></tr>
                    ) : filteredDrugs.map(d => (
                      <tr key={d.id} className="border-b border-slate-100" data-testid={`drug-row-${d.id}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-slate-800">{d.name}</div>
                          <div className="text-xs text-slate-400">{d.generic_name}</div>
                        </td>
                        <td className="px-4 py-2.5 text-slate-600">{d.strength || '—'}</td>
                        <td className="px-4 py-2.5 text-slate-600">{d.dosage_form || '—'}</td>
                        <td className="px-4 py-2.5 text-slate-600">{d.manufacturer || '—'}</td>
                        <td className="px-4 py-2.5 text-right font-semibold">{d.stock_qty ?? 0}</td>
                        <td className="px-4 py-2.5 text-right text-slate-500">{d.reorder_level || 10}</td>
                        <td className="px-4 py-2.5 text-center">
                          {d.low_stock
                            ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Low Stock</Badge>
                            : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">OK</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ BATCHES TAB ============ */}
        <TabsContent value="batches" className="space-y-4">
          <div className="flex items-center justify-end gap-3">
            {canManageInventory && (
              <BatchDialog open={batchDialogOpen} setOpen={setBatchDialogOpen} drugs={drugs} onSaved={fetchAll} />
            )}
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              {loading ? <Skeleton className="h-64 m-4" /> : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Drug</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Batch No.</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Expiry</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">Qty</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">MRP</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Supplier</th>
                      <th className="text-center px-4 py-2 font-semibold text-slate-600">Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400 italic">No batches yet. Add stock by clicking "Add Batch".</td></tr>
                    ) : batches.map(b => (
                      <tr key={b.id} className={`border-b border-slate-100 ${b.is_expired ? 'bg-rose-50' : b.near_expiry ? 'bg-amber-50' : ''}`} data-testid={`batch-row-${b.id}`}>
                        <td className="px-4 py-2.5 text-slate-800">
                          {b.drug_name} <span className="text-slate-400 text-xs">{b.drug_strength}</span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-600">{b.batch_no}</td>
                        <td className="px-4 py-2.5 text-slate-600">{b.expiry}</td>
                        <td className="px-4 py-2.5 text-right font-semibold">{b.qty} <span className="text-slate-300 text-xs">/ {b.original_qty}</span></td>
                        <td className="px-4 py-2.5 text-right">₹{Number(b.mrp || 0).toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-slate-600">{b.supplier || '—'}</td>
                        <td className="px-4 py-2.5 text-center">
                          {b.is_expired
                            ? <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Expired</Badge>
                            : b.near_expiry
                              ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Near Expiry</Badge>
                              : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {canManageInventory && b.qty === b.original_qty && (
                            <Button variant="ghost" size="sm" onClick={async () => {
                              try {
                                await pharmacyAPI.deleteBatch(b.id);
                                toast.success('Batch removed');
                                fetchAll();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Could not delete batch');
                              }
                            }} data-testid={`delete-batch-${b.id}`}>
                              <Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-500" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ DISPENSES TAB ============ */}
        <TabsContent value="dispenses" className="space-y-4">
          <div className="flex items-center justify-end gap-3">
            {canDispense && (
              <DispenseDialog
                open={dispenseDialogOpen}
                setOpen={setDispenseDialogOpen}
                drugs={drugs}
                onSaved={(newDispense) => {
                  fetchAll();
                  if (newDispense?.id) {
                    window.open(`/pharmacy/print/${newDispense.id}`, '_blank');
                  }
                }}
              />
            )}
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              {loading ? <Skeleton className="h-64 m-4" /> : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Dispense No.</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Date</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">Customer</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">Items</th>
                      <th className="text-right px-4 py-2 font-semibold text-slate-600">Total</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-600">By</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {dispenses.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400 italic">No dispenses yet.</td></tr>
                    ) : dispenses.map(d => (
                      <tr key={d.id} className="border-b border-slate-100" data-testid={`dispense-row-${d.id}`}>
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{d.dispense_no}</td>
                        <td className="px-4 py-2.5 text-slate-600">{d.created_at ? format(new Date(d.created_at), 'MMM d, HH:mm') : '—'}</td>
                        <td className="px-4 py-2.5 text-slate-700">{d.patient_name || d.patient_id || '—'}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600">{(d.items || []).length}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-emerald-700 inline-flex items-center justify-end gap-1 w-full">
                          <IndianRupee className="h-3.5 w-3.5" /> {Number(d.grand_total || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs">{d.dispensed_by_name}</td>
                        <td className="px-4 py-2.5 text-right">
                          <Button variant="ghost" size="sm" onClick={() => window.open(`/pharmacy/print/${d.id}`, '_blank')} data-testid={`print-dispense-${d.id}`} title="Print Bill">
                            <Printer className="h-4 w-4 text-emerald-600" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Print Receipt Dialog */}
      {printDispense && (
        <PrintReceiptDialog dispense={printDispense} onClose={() => setPrintDispense(null)} />
      )}
    </div>
  );
}

// ===========================================================================
// Sub-components
// ===========================================================================
const SummaryCard = ({ testid, icon: Icon, color, label, value, sub }) => {
  const ring = {
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    rose: 'bg-rose-100 text-rose-600',
    indigo: 'bg-indigo-100 text-indigo-600',
  }[color] || 'bg-slate-100 text-slate-600';
  return (
    <Card className="card-hover" data-testid={testid}>
      <CardContent className="p-5 flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1.5">{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg ${ring}`}><Icon className="h-5 w-5" /></div>
      </CardContent>
    </Card>
  );
};

const DrugDialog = ({ open, setOpen, onSaved }) => {
  const [form, setForm] = useState({
    name: '', generic_name: '', strength: '', dosage_form: 'Tablet',
    manufacturer: '', schedule: '', hsn_code: '', gst_pct: 12, reorder_level: 10, notes: '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Drug name is required'); return; }
    setSaving(true);
    try {
      await pharmacyAPI.createDrug({ ...form, gst_pct: Number(form.gst_pct), reorder_level: Number(form.reorder_level) });
      toast.success('Drug added to master');
      setOpen(false);
      setForm({ name: '', generic_name: '', strength: '', dosage_form: 'Tablet', manufacturer: '', schedule: '', hsn_code: '', gst_pct: 12, reorder_level: 10, notes: '' });
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save drug');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-drug-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Drug
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Drug to Master</DialogTitle>
          <DialogDescription>Once created, you can add batches in the "Batches" tab.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Name *"><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="drug-form-name" /></FormField>
          <FormField label="Generic Name"><Input value={form.generic_name} onChange={e => setForm({...form, generic_name: e.target.value})} data-testid="drug-form-generic" /></FormField>
          <FormField label="Strength"><Input placeholder="500 mg" value={form.strength} onChange={e => setForm({...form, strength: e.target.value})} data-testid="drug-form-strength" /></FormField>
          <FormField label="Form">
            <Select value={form.dosage_form} onValueChange={(v) => setForm({...form, dosage_form: v})}>
              <SelectTrigger data-testid="drug-form-form"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['Tablet', 'Capsule', 'Syrup', 'Injection', 'Ointment', 'Drops', 'Inhaler', 'Other'].map(x =>
                  <SelectItem key={x} value={x}>{x}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Manufacturer"><Input value={form.manufacturer} onChange={e => setForm({...form, manufacturer: e.target.value})} data-testid="drug-form-mfr" /></FormField>
          <FormField label="Schedule">
            <Select value={form.schedule} onValueChange={(v) => setForm({...form, schedule: v === '__none' ? '' : v})}>
              <SelectTrigger data-testid="drug-form-schedule"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">None</SelectItem>
                <SelectItem value="H">H</SelectItem>
                <SelectItem value="H1">H1</SelectItem>
                <SelectItem value="X">X</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="HSN Code"><Input value={form.hsn_code} onChange={e => setForm({...form, hsn_code: e.target.value})} data-testid="drug-form-hsn" /></FormField>
          <FormField label="GST %"><Input type="number" value={form.gst_pct} onChange={e => setForm({...form, gst_pct: e.target.value})} data-testid="drug-form-gst" /></FormField>
          <FormField label="Reorder Level"><Input type="number" value={form.reorder_level} onChange={e => setForm({...form, reorder_level: e.target.value})} data-testid="drug-form-reorder" /></FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="drug-form-save">
            {saving ? 'Saving…' : 'Save Drug'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BatchDialog = ({ open, setOpen, drugs, onSaved }) => {
  const [form, setForm] = useState({ drug_id: '', batch_no: '', expiry: '', qty: '', mrp: '', purchase_price: '', supplier: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.drug_id || !form.batch_no || !form.expiry || !form.qty || !form.mrp) {
      toast.error('Drug, batch number, expiry, qty and MRP are required');
      return;
    }
    setSaving(true);
    try {
      await pharmacyAPI.createBatch({
        ...form,
        qty: Number(form.qty),
        mrp: Number(form.mrp),
        purchase_price: form.purchase_price ? Number(form.purchase_price) : null,
      });
      toast.success('Batch added to inventory');
      setOpen(false);
      setForm({ drug_id: '', batch_no: '', expiry: '', qty: '', mrp: '', purchase_price: '', supplier: '' });
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save batch');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-batch-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Batch
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Batch</DialogTitle>
          <DialogDescription>Adds new stock for a drug.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Drug *" className="md:col-span-2">
            <Select value={form.drug_id} onValueChange={(v) => setForm({...form, drug_id: v})}>
              <SelectTrigger data-testid="batch-form-drug"><SelectValue placeholder="Choose a drug…" /></SelectTrigger>
              <SelectContent>
                {drugs.map(d => <SelectItem key={d.id} value={d.id}>{d.name} {d.strength}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Batch No. *"><Input value={form.batch_no} onChange={e => setForm({...form, batch_no: e.target.value})} data-testid="batch-form-no" /></FormField>
          <FormField label="Expiry *"><Input type="date" value={form.expiry} onChange={e => setForm({...form, expiry: e.target.value})} data-testid="batch-form-expiry" /></FormField>
          <FormField label="Quantity *"><Input type="number" value={form.qty} onChange={e => setForm({...form, qty: e.target.value})} data-testid="batch-form-qty" /></FormField>
          <FormField label="MRP (₹) *"><Input type="number" step="0.01" value={form.mrp} onChange={e => setForm({...form, mrp: e.target.value})} data-testid="batch-form-mrp" /></FormField>
          <FormField label="Purchase Price (₹)"><Input type="number" step="0.01" value={form.purchase_price} onChange={e => setForm({...form, purchase_price: e.target.value})} data-testid="batch-form-purchase" /></FormField>
          <FormField label="Supplier"><Input value={form.supplier} onChange={e => setForm({...form, supplier: e.target.value})} data-testid="batch-form-supplier" /></FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="batch-form-save">
            {saving ? 'Saving…' : 'Save Batch'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DispenseDialog = ({ open, setOpen, drugs, onSaved }) => {
  const [customer, setCustomer] = useState('');
  const [items, setItems] = useState([{ drug_id: '', qty: 1 }]);
  const [discountPct, setDiscountPct] = useState(0);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCustomer(''); setItems([{ drug_id: '', qty: 1 }]); setDiscountPct(0); setPaymentMode('Cash'); setNotes('');
  };

  const addItem = () => setItems([...items, { drug_id: '', qty: 1 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => setItems(items.map((it, idx) => idx === i ? { ...it, [field]: val } : it));

  const preview = useMemo(() => {
    let subtotal = 0, gst = 0;
    items.forEach(it => {
      const d = drugs.find(x => x.id === it.drug_id);
      if (!d) return;
      // We don't know exact batch MRP here without an extra call; show a rough estimate using last known batch
      // For real accuracy, the backend will recompute. So this is illustrative only.
    });
    return { subtotal, gst, total: subtotal + gst };
  }, [items, drugs]);

  const save = async () => {
    if (!customer.trim()) { toast.error('Customer / patient name is required'); return; }
    if (items.length === 0 || items.some(it => !it.drug_id || !it.qty)) {
      toast.error('All items need a drug and quantity'); return;
    }
    setSaving(true);
    try {
      const { data } = await pharmacyAPI.createDispense({
        patient_name: customer,
        items: items.map(it => ({ drug_id: it.drug_id, qty: Number(it.qty) })),
        discount_pct: Number(discountPct) || 0,
        payment_mode: paymentMode,
        notes,
      });
      toast.success(`${data.dispense_no} — total ₹${data.grand_total.toFixed(2)}`);
      reset();
      setOpen(false);
      onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not dispense');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-dispense-btn">
          <Plus className="h-4 w-4 mr-1" /> New Dispense
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>New Dispense</DialogTitle>
          <DialogDescription>
            Backend picks oldest-expiry batch (FIFO) automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <FormField label="Patient / Customer *">
            <Input value={customer} onChange={e => setCustomer(e.target.value)} placeholder="Walk-in Mona / Patient name" data-testid="dispense-form-customer" />
          </FormField>
          <div className="space-y-2">
            <Label>Items</Label>
            {items.map((it, i) => (
              <div key={i} className="flex items-end gap-2" data-testid={`dispense-item-${i}`}>
                <div className="flex-1">
                  <Select value={it.drug_id} onValueChange={v => updateItem(i, 'drug_id', v)}>
                    <SelectTrigger data-testid={`dispense-item-drug-${i}`}><SelectValue placeholder="Pick a drug…" /></SelectTrigger>
                    <SelectContent>
                      {drugs.filter(d => (d.stock_qty || 0) > 0).map(d =>
                        <SelectItem key={d.id} value={d.id}>
                          {d.name} {d.strength} <span className="text-slate-400 text-xs">· {d.stock_qty} in stock</span>
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-24">
                  <Input type="number" min={1} value={it.qty} onChange={e => updateItem(i, 'qty', e.target.value)} data-testid={`dispense-item-qty-${i}`} />
                </div>
                {items.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => removeItem(i)} data-testid={`dispense-item-remove-${i}`}>
                    <Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-500" />
                  </Button>
                )}
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addItem} data-testid="dispense-add-item">
              <Plus className="h-4 w-4 mr-1" /> Add another item
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <FormField label="Discount %"><Input type="number" min={0} max={100} value={discountPct} onChange={e => setDiscountPct(e.target.value)} data-testid="dispense-discount" /></FormField>
            <FormField label="Payment">
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger data-testid="dispense-payment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Cash', 'UPI', 'Card', 'Insurance', 'Account'].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Notes"><Input value={notes} onChange={e => setNotes(e.target.value)} data-testid="dispense-notes" /></FormField>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dispense-save">
            {saving ? 'Dispensing…' : 'Dispense & Print'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PrintReceiptDialog = ({ dispense, onClose }) => {
  const print = () => {
    const w = window.open('', '_blank', 'width=380,height=600');
    if (!w) { toast.error('Allow popups to print'); return; }
    const html = `
      <html><head><title>${dispense.dispense_no}</title>
      <style>
        body { font-family: monospace; padding: 16px; font-size: 12px; color: #000; }
        h2 { text-align: center; margin: 0 0 4px; font-size: 14px; }
        .meta { text-align: center; font-size: 11px; color: #555; }
        hr { border: none; border-top: 1px dashed #888; margin: 8px 0; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 3px 0; }
        .right { text-align: right; }
        .total { font-weight: bold; font-size: 13px; }
      </style></head>
      <body>
        <h2>Klick Niramaya HMS</h2>
        <div class="meta">Pharmacy Receipt</div>
        <div class="meta">${dispense.dispense_no}</div>
        <div class="meta">${new Date(dispense.created_at).toLocaleString()}</div>
        <hr/>
        <div>Customer: ${dispense.patient_name || dispense.patient_id || '—'}</div>
        <div>By: ${dispense.dispensed_by_name || '—'}</div>
        <hr/>
        <table>
          <thead><tr><th style="text-align:left">Item</th><th class="right">Qty</th><th class="right">Total</th></tr></thead>
          <tbody>
            ${(dispense.items || []).map(i => `
              <tr><td>${i.drug_name} ${i.drug_strength || ''}</td>
              <td class="right">${i.qty}</td>
              <td class="right">₹${Number(i.total || 0).toFixed(2)}</td></tr>
            `).join('')}
          </tbody>
        </table>
        <hr/>
        <table>
          <tr><td>Subtotal</td><td class="right">₹${Number(dispense.subtotal || 0).toFixed(2)}</td></tr>
          <tr><td>GST</td><td class="right">₹${Number(dispense.gst_amount || 0).toFixed(2)}</td></tr>
          ${dispense.discount_amount ? `<tr><td>Discount (${dispense.discount_pct}%)</td><td class="right">- ₹${Number(dispense.discount_amount).toFixed(2)}</td></tr>` : ''}
          <tr class="total"><td>TOTAL</td><td class="right">₹${Number(dispense.grand_total || 0).toFixed(2)}</td></tr>
          <tr><td>Mode</td><td class="right">${dispense.payment_mode || 'Cash'}</td></tr>
        </table>
        <hr/>
        <div style="text-align:center;font-size:10px;color:#888">Thank you · No exchange / refund on medicines</div>
        <script>setTimeout(() => window.print(), 100);</script>
      </body></html>
    `;
    w.document.write(html);
    w.document.close();
  };

  return (
    <Dialog open={!!dispense} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Receipt {dispense.dispense_no}</DialogTitle>
          <DialogDescription>Preview · use Print for thermal receipt.</DialogDescription>
        </DialogHeader>
        <div className="font-mono text-xs bg-slate-50 p-4 rounded">
          <div className="text-center font-bold">Klick Niramaya HMS</div>
          <div className="text-center text-slate-500">Pharmacy Receipt · {dispense.dispense_no}</div>
          <div className="text-center text-slate-500">{new Date(dispense.created_at).toLocaleString()}</div>
          <hr className="my-2 border-dashed" />
          <div>Customer: {dispense.patient_name || dispense.patient_id || '—'}</div>
          <div>By: {dispense.dispensed_by_name || '—'}</div>
          <hr className="my-2 border-dashed" />
          {(dispense.items || []).map((i, k) => (
            <div key={k} className="flex justify-between">
              <span>{i.drug_name} {i.drug_strength} × {i.qty}</span>
              <span>₹{Number(i.total || 0).toFixed(2)}</span>
            </div>
          ))}
          <hr className="my-2 border-dashed" />
          <div className="flex justify-between"><span>Subtotal</span><span>₹{Number(dispense.subtotal || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span>GST</span><span>₹{Number(dispense.gst_amount || 0).toFixed(2)}</span></div>
          {dispense.discount_amount ? (
            <div className="flex justify-between"><span>Discount ({dispense.discount_pct}%)</span><span>- ₹{Number(dispense.discount_amount).toFixed(2)}</span></div>
          ) : null}
          <div className="flex justify-between font-bold text-sm pt-1"><span>TOTAL</span><span>₹{Number(dispense.grand_total || 0).toFixed(2)}</span></div>
          <div className="flex justify-between text-slate-500"><span>Mode</span><span>{dispense.payment_mode || 'Cash'}</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={print} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="print-receipt-btn">
            <Printer className="h-4 w-4 mr-2" /> Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FormField = ({ label, children, className = '' }) => (
  <div className={className}>
    <Label className="text-xs font-semibold text-slate-500 uppercase mb-1.5">{label}</Label>
    {children}
  </div>
);
